import { ActivityTimeline } from '@magnetic/activity-timeline'
import { Card } from '@magnetic/card'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Notification } from '@magnetic/notification'
import { Text } from '@magnetic/text'
import { Button } from '@magnetic/button'
import { Clock, ArrowsClockwise } from '@/lib/icons'
import { timestampToDate } from '@/lib/protobuf-utils'
import type { Timestamp } from '@bufbuild/protobuf/wkt'

interface ApplicationActivityTimelineProps {
  application: {
    state?: string
    stateMessage?: string
    stateUpdatedAt?: Timestamp
    imageDeploymentStatus?: string
    imageDeploymentMessage?: string
    ecrRepository?: string
  }
  onRetryHelm?: () => void
  onRetryCRD?: () => void
  onRetrySetup?: () => void
  onStartValidation?: () => void
  isRetrying?: boolean
  isStartingValidation?: boolean
}

export function ApplicationActivityTimeline({
  application,
  onRetryHelm,
  onRetryCRD,
  onRetrySetup,
  onStartValidation,
  isRetrying,
  isStartingValidation
}: ApplicationActivityTimelineProps) {
  const state = application.state || 'Created'

  // Map our state machine to Magnetic's status types
  // Helm Validation Status
  const getHelmStatus = (): 'complete' | 'inProgress' | 'error' | 'notStarted' => {
    if (state === 'HelmValidationFailed') return 'error'
    if (state === 'ValidatingHelm') return 'inProgress'
    if (state === 'CreatingCRD' || state === 'Syncing' || state === 'Healthy' || state === 'OutOfSync' || state === 'Degraded' || state === 'Suspended' || state === 'Unknown' || state === 'Deleting' || state === 'Deleted') {
      return 'complete'
    }
    return 'notStarted'
  }

  // CRD Creation Status
  const getCRDStatus = (): 'complete' | 'inProgress' | 'error' | 'notStarted' => {
    if (state === 'CreatingCRD') return 'inProgress'
    if (state === 'CRDCreationFailed') return 'error'
    if (state === 'Syncing' || state === 'Healthy' || state === 'OutOfSync' || state === 'Degraded' || state === 'Suspended' || state === 'Unknown' || state === 'Deleting' || state === 'Deleted') {
      return 'complete'
    }
    return 'notStarted'
  }

  // Initial Sync Status
  const getSyncStatus = (): 'complete' | 'inProgress' | 'error' | 'notStarted' => {
    if (state === 'Syncing') return 'inProgress'
    if (state === 'Healthy') return 'complete'
    if (state === 'OutOfSync' || state === 'Degraded') return 'error'
    if (state === 'Suspended' || state === 'Unknown') return 'inProgress'
    return 'notStarted'
  }

  const getCreatedStatus = (): 'complete' | 'inProgress' | 'error' | 'notStarted' => {
    if (state === 'RepoValidationFailed' || state === 'AWSProvisioningFailed') return 'error'
    if (state === 'Created') return 'inProgress'
    return 'complete'
  }

  const helmStatus = getHelmStatus()
  const crdStatus = getCRDStatus()
  const syncStatus = getSyncStatus()
  const createdStatus = getCreatedStatus()

  const currentStep = (() => {
    switch (state) {
      case 'Created':
      case 'RepoValidationFailed':
      case 'AWSProvisioningFailed':
        return 'created'
      case 'ValidatingHelm':
      case 'HelmValidationFailed':
        return 'helm'
      case 'CreatingCRD':
      case 'CRDCreationFailed':
        return 'crd'
      default:
        return 'sync'
    }
  })()

  // Parse timestamp from protobuf Timestamp
  const getTimestamp = () => {
    const date = timestampToDate(application.stateUpdatedAt)
    return date ? date.getTime() : Date.now()
  }

  // Build timeline items array
  const timelineItems: React.ReactElement[] = []

  // Created Step
  timelineItems.push(
    <ActivityTimeline.Item
      key="created"
      heading="Application Created"
      status={createdStatus}
      time={createdStatus !== 'notStarted' ? getTimestamp() : undefined}
      isOpen={currentStep === 'created'}
    >
      <Flex direction="vertical" gap="md">
        <Text size="sm">
          The application record exists and is ready for validation.
        </Text>
        {createdStatus === 'error' && (
          <>
            <Notification status="negative" title="Setup failed">
              <Text size="sm">{application.stateMessage || 'Repository/AWS setup failed'}</Text>
            </Notification>
            {onRetrySetup && (
              <Flex justify="flex-end">
                <Button
                  size="sm"
                  onClick={onRetrySetup}
                  disabled={isRetrying}
                  icon={<ArrowsClockwise size={16} />}
                >
                  Retry Setup
                </Button>
              </Flex>
            )}
          </>
        )}
        {state === 'Created' && (
          <Button
            kind="primary"
            size="sm"
            onClick={onStartValidation}
            disabled={isStartingValidation}
          >
            {isStartingValidation ? 'Starting...' : 'Start Validation'}
          </Button>
        )}
      </Flex>
    </ActivityTimeline.Item>
  )

  // Helm Validation Step
  timelineItems.push(
    <ActivityTimeline.Item
      key="helm"
      heading="Helm Chart Validation"
      status={helmStatus}
      time={helmStatus !== 'notStarted' ? getTimestamp() : undefined}
      isOpen={currentStep === 'helm'}
    >
      <Flex direction="vertical" gap="md">
        {helmStatus === 'error' && (
          <>
            <Notification status="negative" title="Helm validation failed">
              <Text size="sm">{application.stateMessage || 'Helm validation failed'}</Text>
            </Notification>
            {onRetryHelm && (
              <Flex justify="flex-end">
                <Button
                  size="sm"
                  onClick={onRetryHelm}
                  disabled={isRetrying}
                  icon={<ArrowsClockwise size={16} />}
                >
                  Retry Validation
                </Button>
              </Flex>
            )}
          </>
        )}
        {helmStatus === 'inProgress' && (
          <Text size="sm" color="light">
            Validating Helm chart structure and syntax...
          </Text>
        )}
      </Flex>
    </ActivityTimeline.Item>
  )

  // CRD Creation Step
  timelineItems.push(
    <ActivityTimeline.Item
      key="crd"
      heading="ArgoCD CRD Creation"
      status={crdStatus}
      time={crdStatus !== 'notStarted' ? getTimestamp() : undefined}
      isOpen={currentStep === 'crd'}
    >
      <Flex direction="vertical" gap="md">
        {crdStatus === 'error' && (
          <>
            <Notification status="negative" title="CRD creation failed">
              <Text size="sm">{application.stateMessage || 'CRD creation failed'}</Text>
            </Notification>
            {onRetryCRD && (
              <Flex justify="flex-end">
                <Button
                  size="sm"
                  onClick={onRetryCRD}
                  disabled={isRetrying}
                  icon={<ArrowsClockwise size={16} />}
                >
                  Retry CRD Creation
                </Button>
              </Flex>
            )}
          </>
        )}
        {crdStatus === 'inProgress' && (
          <Text size="sm" color="light">
            Creating ArgoCD Application resource in Kubernetes...
          </Text>
        )}
      </Flex>
    </ActivityTimeline.Item>
  )

  // Initial Sync Step
  timelineItems.push(
    <ActivityTimeline.Item
      key="sync"
      heading="Initial Sync"
      status={syncStatus}
      time={syncStatus !== 'notStarted' ? getTimestamp() : undefined}
      isOpen={currentStep === 'sync'}
    >
      <Flex direction="vertical" gap="md">
        {syncStatus === 'inProgress' && (
          <Text size="sm" color="light">
            Syncing application resources to the cluster...
          </Text>
        )}
        {syncStatus === 'error' && (
          <Text size="sm" color="negative">
            Application is in a degraded state. Check the application status below for details.
          </Text>
        )}
      </Flex>
    </ActivityTimeline.Item>
  )

  return (
    <Card>
      <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
        <Flex align="center" gap="sm">
          <Clock size={20} />
          <Heading size="sub-section">Setup Progress</Heading>
        </Flex>

        <ActivityTimeline isCollapsible isRelative>
          {timelineItems}
        </ActivityTimeline>
      </Flex>
    </Card>
  )
}
